import {html} from "../libs/lit.dist.js";
import {WebElement, addScriptFile} from "./abstract-element.js";
import defaultConfig from "../ckeditor5-custom/ck-editor-default-config.js";
import {cancelDomEvent} from "../util/browser.js";

let isLibAdded = false;

// Wczytuje zależności
if (POLKURIER.ENVIRONMENT === 'ADMIN' && !isLibAdded) {
    addScriptFile('/script/ckeditor5-custom/ckeditor.js');
    isLibAdded = true;
}

/**
 * Edytor tekstowy WYSIWYG (CKEditor5)
 *
 * Atrybuty:
 *  {object}   options    - Opcje dla edytora CKEditor5 @see https://ckeditor.com/docs/ckeditor5/latest/
 *  {boolean}  readonly   - Tylko do odczytu (blokuje edycję)
 *  {string}   value      - Treść do edycji
 *
 *  Zdarzenia:
 *   change - zdarzenie po zmianie zawartości

 * Sposób użycia w LIT:
 *  <pk-editor
 *      .value="${model.html}"
 *      @change="${e => model.html = e.target.value"
 * ></pk-editor>
 */
export class PkEditor extends WebElement {

    static get formAssociated() {
        return true;
    }

    static get properties() {
        return {
            options: {type: Object},
            simple: {type: Boolean},
            readonly: {type: Boolean},
            disabled: {type: Boolean},
            name: {type: String},
            toolbar: {type: String},
            imageToolbar: {type: String},
            imageStyles: {type: String},
            table: {type: String},
            uploadFile: {},
            value: {},
        };
    }

    set toolbar(value) {
        this.__toolbar = String(value).split(',');
    }

    get toolbar() {
        return this.__toolbar;
    }

    set imageToolbar(value) {
        this.__imageToolbar = String(value).split(',');
    }

    get imageToolbar() {
        return this.__imageToolbar;
    }

    set imageStyles(value) {
        this.__imageStyles = String(value).split(',');
    }

    get imageStyles() {
        return this.__imageStyles;
    }

    set table(value) {
        this.__table = String(value).split(',');
    }

    get table() {
        return this.__table;
    }

    /**
     * @inheritDoc
     */
    constructor() {
        super();
        this.__scheduledDestructionTimer = null;
        this.internals = this.attachInternals();
        this.editorId = _.uniqueId('editor');
        this.options = {};
        this.value = '';
        this.name = '';
        this.readonly = false;
        this.initialized = false;
        this.disabled = false;
        this.__toolbar = defaultConfig.toolbar.items || [];
        this.__imageToolbar = defaultConfig.image.toolbar || [];
        this.__imageStyles = defaultConfig.image.styles || [];
        this.__table = defaultConfig.table.contentToolbar || [];

        if (POLKURIER.ENVIRONMENT === 'FRONT' && !isLibAdded) {
            addScriptFile('/assets/libraries/ckeditor.js');
            isLibAdded = true;
        }
    }

    set value(value) {
        const previousValue = this._value;
        this._value = value;
        if (previousValue !== value && this.editor) {
            this.editor.setData(value);
        }
    }

    get value() {
        return this._value;
    }

    firstUpdated() {
        super.firstUpdated();
        this.querySelector('.pk-editor-inner').addEventListener('change', cancelDomEvent());
        setTimeout(() => {
            this.initializeEditor().then();
        }, 0);
    }

    connectedCallback() {
        super.connectedCallback();
        clearTimeout(this.__scheduledDestructionTimer);
        this.updateFromValue();
    }

    disconnectedCallback() {
        this.scheduleDestroy();
        super.disconnectedCallback();
    }

    scheduleDestroy() {
        clearTimeout(this.__scheduledDestructionTimer);
        this.__scheduledDestructionTimer = setTimeout(() => this.destroyEditor(), 100);
    }

    destroyEditor() {
        console.warn('DESTROY_EDITOR')
        if (this.editor) {
            this.editor.destroy();
            this.editor = null;
            this.initialized = false;
        }
    }

    updated(t) {
        super.updated(t);
        if (t.has('disabled')) {
            this.updateFromValue();
        }
    }

    updateFromValue() {
        this.internals.setFormValue(!this.disabled ? this._value : null);
    }

    async reinitializeEditor() {
        this.destroyEditor();
        await this.initializeEditor();
    }

    async initializeEditor() {
        if (!this.initialized) {
            if (!window.ClassicEditor) {
                setTimeout(() => this.initializeEditor(), 100);
                return;
            }
            this.initialized = true;
            try {
                const element = this.querySelector('.pk-editor--editor-container');
                this.editor = await window.ClassicEditor.create(element, {
                    ...defaultConfig,
                    toolbar: {
                        items: this.__toolbar,
                        image: {
                            toolbar: this.__imageToolbar,
                            styles: this.__imageStyles,
                        },
                        table: {
                            contentToolbar: this.__table,
                        },
                    },
                    mediaEmbed: {
                        previewsInData: true
                    },
                    ...(this.options.options || {})
                });

                this.editor.plugins.get('FileRepository').createUploadAdapter = loader => ({
                    upload: () => {
                        return loader.file.then(file => new Promise(async (resolve, reject) => {
                            if (typeof this.uploadFile !== 'function') {
                                reject();
                            }
                            try {
                                const data = await this.uploadFile(file);
                                resolve({
                                    // Generuje URL dla SRC
                                    default: data.url
                                });
                            } catch (error) {
                                reject();
                            }
                        }));
                    },
                    abort: () => {},
                });

                this.editor.setData(this.value);
                this.updateReadOnlyMode();

                this.editor.model.document.on('change:data', () => {
                    this._value = this.editor.getData();
                    this.dispatchEvent(new Event('change'));
                    this.updateFromValue();
                    this.requestUpdate();
                });

                this.requestUpdate();
            } catch (e) {
                console.error(e);
            }
        }
    }

    updateReadOnlyMode() {
        if (this.editor) {
            this.editor.isReady = this.readonly;
        }
    }

    render() {
        this.updateReadOnlyMode();
        return html`
            ${!this.initialized ? html`<pk-spinner center></pk-spinner>` : null}
            <div class="pk-editor-inner">
                <div class="pk-editor--editor-container" id="${this.editorId}"></div>
            </div>
        `;
    }

}

customElements.define('pk-editor', PkEditor);
